import React, { useEffect, useState } from 'react';

import {
  DatabaseOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  SaveOutlined,
  TableOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Steps,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ApiErrorData } from 'models/ApiResponse';
import { DiIngestJob } from 'models/DiIngestJob';
import { DiParsingFormat } from 'models/DiParsingFormat';

import {
  DiIngestJobService,
  MatchColumnsRowSchema,
} from 'services/DiIngestJobService';
import { DiTableColumn } from 'services/DiTableColumn';

import appI18n from 'config/i18n';

import { useThemeLayoutConfig } from '../../../../config/ThemeDashboardLayoutConfig';

const { Text } = Typography;
const { Option } = Select;
const { Step } = Steps;

interface ColumnMappingModalProps {
  isOpen: boolean;
  handleModalCancel: () => void;
  jobId: number;
  fieldsFromDataBase: DiTableColumn[];
  parsingFormats: DiParsingFormat[];
  saveColumnMapping: (
    job: DiIngestJob,
    mappings: MatchColumnsRowSchema[]
  ) => Promise<void>;
}

const ColumnMappingModal: React.FC<ColumnMappingModalProps> = ({
  isOpen,
  handleModalCancel,
  jobId,
  fieldsFromDataBase,
  parsingFormats,
  saveColumnMapping,
}) => {
  const { theme } = useThemeLayoutConfig();
  const primaryColor = theme?.token?.colorPrimary || '#1890ff';

  const [selectedJob, setSelectedJob] = useState<DiIngestJob>();
  // Columnas del documento (extraídas)
  const [documentColumns, setDocumentColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Current step
  const [currentStep, setCurrentStep] = useState<number>(0);

  // Mapeo entre columnas del documento y campos de BD
  const [mappings, setMappings] = useState<
    Record<
      string,
      {
        field: string;
        label: string;
        type: string;
        format: string;
      }
    >
  >({});

  // Campos de BD que ya están mapeados (para evitar duplicados)
  const [mappedFields, setMappedFields] = useState<string[]>([]);

  // Estado para controlar si el mapeo es válido para guardar
  const [isMappingValid, setIsMappingValid] = useState<boolean>(false);

  const { t } = useTranslation(undefined, { i18n: appI18n });

  const dateFormat: string = useSelector(
    (state: any) => state.intl?.dateFormat
  );

  // Obtener los campos requeridos directamente desde las columnas de BD
  const requiredFields = fieldsFromDataBase
    .filter((field) => field.isRequired)
    .map((field) => field.columnName);

  // Obtener los campos requeridos que faltan
  const getMissingRequiredFields = () => {
    const mappedRequiredFields = requiredFields.filter((field) =>
      Object.values(mappings).some((mapping) => mapping.field === field)
    );
    return requiredFields.filter(
      (field) => !mappedRequiredFields.includes(field)
    );
  };

  // Obtener los nombres de los campos requeridos que faltan
  const getMissingRequiredFieldNames = () => {
    const missingFields = getMissingRequiredFields();
    return missingFields.map(
      (fieldName) =>
        fieldsFromDataBase.find((f) => f.columnName === fieldName)?.title ||
        fieldName
    );
  };

  // Traducir tipos de campo para mostrar en la interfaz
  const translateFieldType = (type: string): string => {
    const typeMap: Record<string, string> = {
      texto: 'Texto',
      text: 'Texto',
      fecha: 'Fecha',
      date: 'Fecha',
      numero: 'Número',
      number: 'Número',
      email: 'Email',
    };
    return typeMap[type.toLowerCase()] || type;
  };

  // Verificar si un campo requiere formato
  const fieldRequiresFormat = (fieldType: string): boolean => {
    // Si el trabajo es de tipo EXCEL, no requerir formato para ningún campo
    if (selectedJob?.fileType === 'EXCEL') {
      return false;
    }

    const normalizedType = fieldType.toLowerCase();
    return (
      normalizedType === 'fecha' ||
      normalizedType === 'date' ||
      normalizedType === 'numero' ||
      normalizedType === 'number'
    );
  };

  // Obtener los campos mapeados que requieren formato pero no lo tienen seleccionado
  const getMissingFormatFields = () => {
    // Si el trabajo es de tipo EXCEL, no requerir formato para ningún campo
    if (selectedJob?.fileType === 'EXCEL') {
      return [];
    }

    return Object.entries(mappings)
      .filter(
        ([, mapping]) =>
          fieldRequiresFormat(mapping.type) &&
          (!mapping.format || mapping.format === '')
      )
      .map(([column, mapping]) => ({
        column,
        field: mapping.label,
        type: mapping.type,
      }));
  };

  // Obtener los nombres de los campos que faltan formato
  const getMissingFormatFieldNames = () => {
    const missingFormatFields = getMissingFormatFields();
    return missingFormatFields.map((item) => `${item.field} (${item.type})`);
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const dateFormats = parsingFormats.filter((record) => {
    return record.type === 'DATE';
  });

  const numberFormats = parsingFormats.filter((record) => {
    return record.type === 'NUMBER';
  });

  const [sortedExtractedColumns, setSortedExtractedColumns] = useState<any[]>(
    []
  );

  const fetchData = async () => {
    setLoading(true);

    // Resetear el mapeo y los campos mapeados cuando se abre el modal
    setMappings({});
    setMappedFields([]);
    setCurrentStep(0);

    const jobResponse = await DiIngestJobService.findById(jobId);

    if (jobResponse.success) {
      const job = jobResponse.data as DiIngestJob;
      setSelectedJob(job);

      // Usar las columnas extraídas reales del trabajo
      if (
        job.extractedColumns &&
        Array.isArray(job.extractedColumns) &&
        job.extractedColumns.length > 0
      ) {
        // Ordenar las columnas extraídas por id de manera ascendente
        const sortedColumns = [...job.extractedColumns].sort(
          (a, b) => a.id - b.id
        );
        setSortedExtractedColumns(sortedColumns);

        // Extraer los nombres de las columnas del documento
        const columnHeaders = sortedColumns.map((col) => col.headerName);
        setDocumentColumns(columnHeaders);

        // Pre-poblar mapeos existentes desde la BD (tableColumnId y parsingFormatId)
        const initialMappings: Record<
          string,
          {
            field: string;
            label: string;
            type: string;
            format: string;
          }
        > = {};
        const alreadyMappedFields: string[] = [];

        sortedColumns.forEach((col) => {
          if (col.tableColumnId) {
            const tableCol = fieldsFromDataBase.find(
              (f) => f.id === col.tableColumnId
            );
            if (tableCol) {
              const formatRecord = col.parsingFormatId
                ? parsingFormats.find((pf) => pf.id === col.parsingFormatId)
                : undefined;

              initialMappings[col.headerName] = {
                field: tableCol.columnName,
                label: tableCol.title,
                type: tableCol.formatType || 'text',
                format: formatRecord ? formatRecord.format : '',
              };
              alreadyMappedFields.push(tableCol.columnName);
            }
          }
        });

        setMappings(initialMappings);
        setMappedFields(alreadyMappedFields);
      } else {
        setDocumentColumns([]);
        message.warning(
          t(
            'scenes.data_ingester.upload_files_history.components.column_mapping_modal.messages.no_columns_found'
          )
        );
      }

      setLoading(false);
    } else {
      const errorData = jobResponse.data as ApiErrorData;
      message.error(
        errorData.message ||
          t(
            'scenes.data_ingester.upload_files_history.components.column_mapping_modal.errors.fetch_job'
          ),
        3.5
      );
      setLoading(false);
      handleModalCancel(); // Cerrar el modal si hay un error
    }
  };

  const handleSaveMapping = async () => {
    if (selectedJob) {
      setLoading(true);

      try {
        // Validar campos requeridos
        const missingRequiredFields = getMissingRequiredFields();

        if (missingRequiredFields.length > 0) {
          setLoading(false);
          return;
        }

        // Validar campos que requieren formato
        const missingFormatFields = getMissingFormatFields();

        if (missingFormatFields.length > 0) {
          setLoading(false);
          return;
        }

        // Transformar el mapeo para enviarlo al backend
        const columnMappings = Object.entries(mappings).map(
          ([columnHeader, mapping]) => {
            // Encontrar la columna extraída correspondiente para obtener su ID numérico
            const extractedColumn = sortedExtractedColumns.find(
              (col) => col.headerName === columnHeader
            );
            const extractedColumnId = extractedColumn?.id || 0;

            // Encontrar el ID de la columna de la tabla en la BD
            const tableColumn = fieldsFromDataBase.find(
              (f) => f.columnName === mapping.field
            );
            const tableColumnId = tableColumn?.id || 0;

            // Encontrar el ID del formato seleccionado (si aplica)
            let parsingFormatId = null;
            if (mapping.format) {
              const format = parsingFormats.find(
                (f) => f.format === mapping.format
              );
              parsingFormatId = format?.id || null;
            }

            return {
              extractedColumnId,
              tableColumnId,
              parsingFormatId,
            };
          }
        );

        // Llamar a la función para guardar
        await saveColumnMapping(
          selectedJob,
          columnMappings.map((record) => {
            return {
              extractedColumnId: record.extractedColumnId,
              tableColumnConfigId: record.tableColumnId,
              parsingFormatId: record.parsingFormatId,
            };
          })
        );

        handleModalCancel();
      } catch (error) {
        console.error('Error al guardar el mapeo:', error);
        message.error(
          t(
            'scenes.data_ingester.upload_files_history.components.column_mapping_modal.errors.save_mapping'
          )
        );
      } finally {
        setLoading(false);
      }
    }
  };

  // Manejar la selección de un campo de BD para una columna
  const handleFieldSelect = (column: string, value: string) => {
    // Si el valor está vacío (cuando se usa allowClear), eliminar el mapeo
    if (!value) {
      handleRemoveMapping(column);
      return;
    }

    const field = fieldsFromDataBase.find((f) => f.columnName === value);

    if (field) {
      // Si el campo ya estaba mapeado a otra columna, eliminar ese mapeo
      const existingMappedColumn = Object.keys(mappings).find(
        (key) => key !== column && mappings[key]?.field === value
      );

      if (existingMappedColumn) {
        const newMappings = { ...mappings };
        delete newMappings[existingMappedColumn];
        setMappings(newMappings);
        message.warning(
          `El campo "${field.title}" fue desmapeado de la columna "${existingMappedColumn}"`
        );
      }

      // Si esta columna ya tenía un campo asignado, quitarlo de la lista de campos mapeados
      if (mappings[column]) {
        setMappedFields((prev) =>
          prev.filter((f) => f !== mappings[column].field)
        );
      }

      // Actualizar el mapeo con el nuevo campo
      const newMappings = { ...mappings };
      newMappings[column] = {
        field: field.columnName,
        label: field.title,
        type: field.formatType || 'text', // Usar 'text' como valor predeterminado si formatType es null
        format: '',
      };

      setMappings(newMappings);

      // Añadir el campo a la lista de campos mapeados
      setMappedFields((prev) => [...prev, field.columnName]);
    }
  };

  // Manejar el cambio de formato para una columna
  const handleFormatChange = (column: string, format: string) => {
    const currentMapping = mappings[column];
    if (currentMapping) {
      const newMappings = { ...mappings };
      newMappings[column] = {
        ...currentMapping,
        format,
      };
      setMappings(newMappings);
    }
  };

  // Función para quitar el mapeo de una columna
  const handleRemoveMapping = (column: string) => {
    const newMappings = { ...mappings };
    if (newMappings[column]) {
      // Eliminar el campo de la lista de campos mapeados
      const fieldToRemove = newMappings[column].field;
      setMappedFields((prev) =>
        prev.filter((field) => field !== fieldToRemove)
      );

      // Eliminar el mapeo
      delete newMappings[column];
      setMappings(newMappings);

      message.success(`Mapeo eliminado para la columna "${column}"`);
    }
  };

  // Renderizar una fila de mapeo para una columna del documento
  const renderColumnMapping = (column: string) => {
    const currentMapping = mappings[column] || null;

    // Si el archivo es de tipo EXCEL, obtener la "addressKey" (columna A, B, etc.)
    const addressKey =
      selectedJob?.fileType === 'EXCEL'
        ? sortedExtractedColumns.find((c) => c.headerName === column)
            ?.addressKey || null
        : null;

    return (
      <div className="mapping-row" key={column}>
        <div className="mapping-field">
          <div className="field-header">
            <span className="field-label">{column}</span>
            {addressKey && (
              <Tag color="green" style={{ marginLeft: 6 }}>
                <FileExcelOutlined /> Columna: {addressKey}
              </Tag>
            )}
          </div>

          <div className="selectors-container">
            <div className="db-field-selector">
              <div className="selector-label">
                <DatabaseOutlined style={{ marginRight: 6 }} /> Campo en la base
                de datos
              </div>
              <Select
                showSearch
                placeholder="Seleccionar campo"
                value={currentMapping?.field || undefined}
                onChange={(value) => handleFieldSelect(column, value)}
                allowClear={true}
                filterOption={filterFieldOption}
                style={{ width: '100%' }}
                optionLabelProp="label"
              >
                {fieldsFromDataBase.map((field) => (
                  <Option
                    key={field.columnName}
                    value={field.columnName}
                    label={field.title}
                    disabled={
                      mappedFields.includes(field.columnName) &&
                      (!currentMapping ||
                        currentMapping.field !== field.columnName)
                    }
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <span>
                        <DatabaseOutlined
                          style={{
                            marginRight: 6,
                            fontSize: '12px',
                            color: '#8c8c8c',
                          }}
                        />
                        {field.title}
                        {field.isRequired && (
                          <span style={{ color: '#ff4d4f', marginLeft: '4px' }}>
                            *
                          </span>
                        )}
                      </span>
                      <Tag
                        color={getFieldTypeColor(field.formatType || 'text')}
                        style={{ fontSize: '11px', margin: 0 }}
                      >
                        {translateFieldType(field.formatType || 'texto')}
                      </Tag>
                    </div>
                  </Option>
                ))}
              </Select>
            </div>
          </div>

          {currentMapping && (
            <div className="selected-mapping-indicator">
              <span className="type-label">Tipo de dato:</span>
              <Tag
                color={getFieldTypeColor(currentMapping.type)}
                style={{ fontSize: '12px' }}
              >
                {translateFieldType(currentMapping.type)}
              </Tag>
            </div>
          )}

          {currentMapping?.type && fieldRequiresFormat(currentMapping.type) && (
            <div>
              <div className="format-selector">
                <span className="type-label">Formato:</span>
                <div
                  className="format-chips"
                  style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}
                >
                  {(currentMapping.type.toLowerCase().includes('fecha') ||
                  currentMapping.type.toLowerCase().includes('date')
                    ? dateFormats
                    : numberFormats
                  ).map((format) => (
                    <Tooltip
                      key={format.id}
                      title={`Ejemplo: ${format.exampleValue}`}
                      placement="top"
                    >
                      <Button
                        type={
                          currentMapping.format === format.format
                            ? 'primary'
                            : 'default'
                        }
                        size="small"
                        onClick={() =>
                          handleFormatChange(column, format.format)
                        }
                        className={`format-chip ${
                          currentMapping.format === format.format
                            ? 'selected'
                            : ''
                        }`}
                        style={{
                          margin: '0 4px 4px 0',
                          ...(currentMapping.format === format.format
                            ? {
                                backgroundColor: primaryColor, // Color primario del tema
                                borderColor: primaryColor,
                                color: '#fff',
                              }
                            : {}),
                        }}
                      >
                        {format.format}
                      </Button>
                    </Tooltip>
                  ))}
                </div>
              </div>

              {!currentMapping.format && selectedJob?.fileType !== 'EXCEL' && (
                <span
                  style={{
                    color: '#ff4d4f',
                    marginLeft: '5px',
                    fontSize: '12px',
                    fontWeight: 'normal',
                  }}
                >
                  <WarningOutlined /> Debe seleccionar un formato
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Renderizar el contenido del paso 1 (Instrucciones)
  const renderStep1 = () => {
    // Mostrar skeleton mientras se obtiene la data inicial
    if (loading && (!selectedJob || documentColumns.length === 0)) {
      return <Skeleton active paragraph={{ rows: 6 }} />;
    }

    return (
      <div className="instructions-step">
        <Alert
          type="info"
          message="Instrucciones"
          description={
            <div>
              <p>
                1. Para cada columna de tu documento, selecciona el campo
                correspondiente de la base de datos.
              </p>
              <p>
                2. Los campos marcados con{' '}
                <span className="required-asterisk">*</span> son obligatorios.
              </p>
              <p>
                3. Para fechas y números, selecciona el formato que coincida con
                tus datos.
              </p>
            </div>
          }
          style={{ marginBottom: 24 }}
          showIcon
        />

        <Card
          className="job-info-card"
          title={
            <div className="card-title">
              <FileTextOutlined style={{ marginRight: 8 }} />
              <span>Información del Archivo</span>
            </div>
          }
        >
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <Descriptions column={1} size="small">
                <Descriptions.Item
                  label={t(
                    'scenes.data_ingester.upload_files_history.components.column_mapping_modal.card.labels.carrier'
                  )}
                >
                  <Text strong>{selectedJob?.client?.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item
                  label={t(
                    'scenes.data_ingester.upload_files_history.components.column_mapping_modal.card.labels.file'
                  )}
                >
                  <Text strong>{selectedJob?.originalFilename}</Text>
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={12}>
              <Descriptions column={1} size="small">
                <Descriptions.Item
                  label={t(
                    'scenes.data_ingester.upload_files_history.components.column_mapping_modal.card.labels.uploaded_at'
                  )}
                >
                  <Text>
                    {dayjs(selectedJob?.createdAt).format(
                      `${dateFormat} HH:mm`
                    )}
                  </Text>
                </Descriptions.Item>
                {/*<Descriptions.Item label="Total de filas">*/}
                {/*  <Text>412</Text>*/}
                {/*</Descriptions.Item>*/}
              </Descriptions>
            </Col>
          </Row>
        </Card>
      </div>
    );
  };

  // Renderizar el contenido del paso 2 (Mapeo de columnas)
  const renderStep2 = () => {
    // Mostrar skeleton mientras se obtiene la data inicial
    if (loading && documentColumns.length === 0) {
      return <Skeleton active paragraph={{ rows: 8 }} />;
    }

    const missingRequiredFieldNames = getMissingRequiredFieldNames();
    const missingFormatFieldNames = getMissingFormatFieldNames();

    return (
      <div className="mapping-step">
        {missingRequiredFieldNames.length > 0 && (
          <Alert
            type="warning"
            message="Campos obligatorios pendientes"
            description={
              <div>
                <p>
                  Los siguientes campos son obligatorios y deben asignarse a
                  alguna columna:
                </p>
                <ul>
                  {missingRequiredFieldNames.map((field) => (
                    <li key={field}>{field}</li>
                  ))}
                </ul>
              </div>
            }
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        {missingFormatFieldNames.length > 0 &&
          selectedJob?.fileType !== 'EXCEL' && (
            <Alert
              type="warning"
              message="Formatos pendientes"
              description={
                <div>
                  <p>
                    Los siguientes campos requieren que selecciones un formato:
                  </p>
                  <ul>
                    {missingFormatFieldNames.map((field) => (
                      <li key={field}>{field}</li>
                    ))}
                  </ul>
                </div>
              }
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}
        <div className="column-mappings">
          {documentColumns.map((column) => renderColumnMapping(column))}
        </div>
      </div>
    );
  };

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  // Función para validar si el mapeo cumple todas las reglas
  const validateMapping = () => {
    // Regla 1: Al menos una columna mapeada
    const hasAtLeastOneMapping = Object.keys(mappings).length > 0;

    // Regla 2: Todos los campos requeridos están asignados
    const missingRequiredFields = getMissingRequiredFields();
    const allRequiredFieldsMapped = missingRequiredFields.length === 0;

    // Regla 3: Todos los campos que requieren formato lo tienen seleccionado
    // Para archivos EXCEL, no requerir formato
    const missingFormatFields = getMissingFormatFields();
    const allFormatsSelected =
      selectedJob?.fileType === 'EXCEL'
        ? true
        : missingFormatFields.length === 0;

    // El mapeo es válido si cumple todas las reglas
    const isValid =
      hasAtLeastOneMapping && allRequiredFieldsMapped && allFormatsSelected;

    setIsMappingValid(isValid);
  };

  // Actualizar la validación cada vez que cambia el mapeo
  useEffect(() => {
    validateMapping();
  }, [mappings]);

  // Obtener color para el tipo de campo
  const getFieldTypeColor = (type: string): string => {
    const normalizedType = type.toLowerCase();
    if (normalizedType.includes('texto') || normalizedType.includes('text')) {
      return 'purple'; // purple preset for text
    } else if (
      normalizedType.includes('fecha') ||
      normalizedType.includes('date')
    ) {
      return 'geekblue'; // geekblue preset for dates
    } else if (
      normalizedType.includes('numero') ||
      normalizedType.includes('number')
    ) {
      return 'volcano'; // volcano preset for numbers
    } else if (normalizedType.includes('email')) {
      return 'magenta';
    }
    return 'default';
  };

  // Función para normalizar texto (eliminar acentos, convertir a minúsculas)
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  // Filtrar campos por texto de búsqueda
  const filterFieldOption = (input: string, option: any) => {
    const fieldName = option?.value || '';
    const field = fieldsFromDataBase.find((f) => f.columnName === fieldName);
    if (!field) return false;

    // Normalizar tanto la entrada como el texto del campo
    const normalizedInput = normalizeText(input);
    const normalizedLabel = normalizeText(field.title);

    return normalizedLabel.includes(normalizedInput);
  };

  return (
    <Modal
      title={
        <div className="modal-header">
          <TableOutlined style={{ marginRight: 8 }} />
          <span>Mapeo de Columnas</span>
        </div>
      }
      open={isOpen}
      onCancel={handleModalCancel}
      width={900}
      footer={null}
      className="column-mapping-modal"
      closable={true}
      maskClosable={false}
    >
      <Divider className="modal-divider" />

      <Steps current={currentStep} className="mapping-steps">
        <Step title="Instrucciones" />
        <Step title="Mapeo de Columnas" />
      </Steps>

      <div className="steps-content">
        {currentStep === 0 && renderStep1()}
        {currentStep === 1 && renderStep2()}
      </div>

      <div className="modal-footer">
        <Space>
          <Button onClick={handleModalCancel} disabled={loading}>
            Cancelar
          </Button>
          {/*{currentStep > 0 && (*/}
          {/*  <Button onClick={prevStep} disabled={loading}>*/}
          {/*    Anterior*/}
          {/*  </Button>*/}
          {/*)}*/}
          {currentStep < 1 && (
            <Button type="primary" onClick={nextStep}>
              Siguiente
            </Button>
          )}
          {currentStep === 1 && (
            <Button
              type="primary"
              onClick={handleSaveMapping}
              loading={loading}
              icon={<SaveOutlined />}
              disabled={!isMappingValid}
            >
              Guardar Mapeo
            </Button>
          )}
        </Space>
      </div>
    </Modal>
  );
};

export default ColumnMappingModal;
